import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getInternalNumber } from 'features/message-center/utils';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { SECONDARY_API_CLIENT } from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';

export const MessageCenterContext = React.createContext();

export const MessageCenterProvider = ({ children }) => {
  const [user] = useUserContext();
  const [internalNumber, setInternalNumber] = useState(null);

  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [onSendNewMessage, setOnSendNewMessage] = useState(null);

  const { state } = useFetchWithStatusV2({
    endpoint: user ? Endpoints.UserPhones : null,
    apiInstance: SECONDARY_API_CLIENT
  });
  const userInternalPhones = state?.results ?? [];

  useEffect(() => {
    setInternalNumber(getInternalNumber(user));
  }, [user]);

  const recipientPhone = recipient?.phone;
  const recipientCode = recipient?.countryCode ?? '+1';
  const recipientPhoneWithCode = recipient?.phone ? `${recipientCode}${recipientPhone}` : null;

  const openDrawer = useCallback(
    ({ recipient: newRecipient, onSendNewMessage: newOnSendNewMessage }) => {
      setRecipient(newRecipient);
      setOnSendNewMessage(() => newOnSendNewMessage);

      const canUseDrawerChat = checkIfFeatureFlagEnabled(FeatureFlags.MessageCenterDrawerChat);
      if (!canUseDrawerChat) return;

      setIsDrawerOpen(true);
    },
    [checkIfFeatureFlagEnabled]
  );

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    onSendNewMessage && onSendNewMessage();
  }, [onSendNewMessage]);

  const handleSelectInternalNumber = useCallback(phone => {
    setInternalNumber(phone);
  }, []);

  const value = useMemo(() => {
    return {
      recipient,
      isDrawerOpen,
      recipientPhoneWithCode,
      openDrawer,
      closeDrawer,
      onSendNewMessage,
      internalNumber,
      userInternalPhones,
      handleSelectInternalNumber
    };
  }, [
    closeDrawer,
    handleSelectInternalNumber,
    internalNumber,
    isDrawerOpen,
    onSendNewMessage,
    openDrawer,
    recipient,
    recipientPhoneWithCode,
    userInternalPhones
  ]);

  return <MessageCenterContext.Provider value={value}>{children}</MessageCenterContext.Provider>;
};

export const useChatContext = () => {
  const context = useContext(MessageCenterContext);
  if (!context) {
    throw new Error('useChatContext must be used within a MessageCenterProvider');
  }
  return context;
};
